import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  XMarkIcon,
  UserIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  EyeIcon,
  EyeSlashIcon
} from '@heroicons/react/24/outline'
import { Usuario, usuariosService } from '../../../services/usuariosService'
import { institucionesService, Institucion } from '../../../services/institucionesService'

interface UsuarioConPassword extends Usuario {
  password?: string
}

interface OpcionRol {
  value: string
  label: string
  description: string
}

const ROLES_GLOBALES: OpcionRol[] = [
  { value: 'usuario', label: 'Usuario', description: 'Acceso base sin privilegios administrativos globales.' },
  { value: 'admin', label: 'Administrador', description: 'Administra usuarios, contenido y configuracion del sistema.' },
  { value: 'superadmin', label: 'Superadmin', description: 'Control total de seguridad, configuracion y operaciones globales.' },
]

const ROLES_INSTITUCIONALES: OpcionRol[] = [
  { value: 'admin', label: 'Institucion Admin', description: 'Gestiona la institucion y a sus miembros.' },
  { value: 'supervisor', label: 'Institucion Supervisor', description: 'Supervisa operacion y coordinacion institucional.' },
  { value: 'jefe', label: 'Institucion Jefe', description: 'Lidera gestion academica y operativa.' },
  { value: 'usuario', label: 'Institucion Usuario', description: 'Acceso basico dentro de la institucion.' },
  { value: 'maestro', label: 'Institucion Maestro', description: 'Acceso docente para trabajo academico.' },
  { value: 'alumno', label: 'Institucion Alumno', description: 'Acceso estudiantil limitado a su contexto.' },
]

const ROLES_EMPRESARIALES: OpcionRol[] = [
  { value: 'admin', label: 'Empresa Admin', description: 'Gestiona la empresa, miembros y operaciones principales.' },
  { value: 'moderador', label: 'Empresa Moderador', description: 'Coordina procesos y apoyo operativo interno.' },
  { value: 'supervisor', label: 'Empresa Supervisor', description: 'Supervisa equipos y seguimiento operativo.' },
  { value: 'contador', label: 'Empresa Contador', description: 'Acceso financiero y control administrativo.' },
  { value: 'usuario', label: 'Empresa Usuario', description: 'Acceso operativo basico dentro de la empresa.' },
]

interface ModalUsuarioProps {
  isOpen: boolean
  onClose: () => void
  tipo: 'ver' | 'editar' | 'crear' | 'eliminar'
  usuario?: Usuario | null
  onGuardar: (usuario: Usuario) => void
  onEliminar: (id: string) => void
}

const ModalUsuario: React.FC<ModalUsuarioProps> = ({
  isOpen,
  onClose,
  tipo,
  usuario,
  onGuardar,
  onEliminar
}) => {
  const [formData, setFormData] = useState<Partial<UsuarioConPassword>>({
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    empresa: '',
    rol_global: 'usuario',
    rol_organizacion: undefined,
    tipo_organizacion: undefined,
    rol: 'usuario',
    estado: 'activo',
    verificado: false,
    suscripcion: 'basica',
    password: ''
  })
  const [errores, setErrores] = useState<Record<string, string>>({})
  const [cargando, setCargando] = useState(false)
  const [mostrarPassword, setMostrarPassword] = useState(false)
  const [instituciones, setInstituciones] = useState<Institucion[]>([])

  const organizacionesEmpresa = instituciones.filter(
    (item) => item.es_empresa || item.tipo_organizacion === 'empresa' || item.origen === 'organizacion'
  )
  const institucionesAcademicas = instituciones.filter(
    (item) => !item.es_empresa && item.origen !== 'organizacion'
  )

  useEffect(() => {
    const cargarInstituciones = async () => {
      try {
        const data = await institucionesService.obtenerInstituciones()
        setInstituciones(data)
      } catch (error) {
        console.error('Error al cargar instituciones', error)
      }
    }
    cargarInstituciones()
  }, [])

  useEffect(() => {
    if (usuario && (tipo === 'ver' || tipo === 'editar' || tipo === 'eliminar')) {
      setFormData(usuario)
    } else if (tipo === 'crear') {
      setFormData({
        nombre: '',
        apellido: '',
        email: '',
        telefono: '',
        empresa: '',
        rol_global: 'usuario',
        rol_organizacion: undefined,
        tipo_organizacion: undefined,
        rol: 'usuario',
        estado: 'activo',
        verificado: false,
        suscripcion: 'basica',
        password: ''
      })
    }
    setErrores({})
  }, [usuario, tipo, isOpen])

  const validarFormulario = (): boolean => {
    const nuevosErrores: Record<string, string> = {}

    if (!formData.nombre?.trim()) {
      nuevosErrores.nombre = 'El nombre es requerido'
    }

    if (!formData.apellido?.trim()) {
      nuevosErrores.apellido = 'El apellido es requerido'
    }

    if (!formData.email?.trim()) {
      nuevosErrores.email = 'El email es requerido'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      nuevosErrores.email = 'El email no es válido'
    }

    if (tipo === 'crear' && !formData.password?.trim()) {
      nuevosErrores.password = 'La contraseña es requerida'
    }

    if (formData.password && formData.password.length < 6) {
      nuevosErrores.password = 'La contraseña debe tener al menos 6 caracteres'
    }

    if (formData.telefono && !/^\+?[\d\s\-\(\)]+$/.test(formData.telefono)) {
      nuevosErrores.telefono = 'El teléfono no es válido'
    }

    if (formData.tipo_organizacion === 'institucion' && !formData.id_institucion) {
      nuevosErrores.id_institucion = 'Debe seleccionar una institución'
    }
    if (formData.tipo_organizacion === 'empresa' && !formData.id_organizacion) {
      nuevosErrores.id_organizacion = 'Debe seleccionar una empresa'
    }

    setErrores(nuevosErrores)
    return Object.keys(nuevosErrores).length === 0
  }

  const manejarSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validarFormulario()) {
      return
    }

    setCargando(true)
    
    try {
      // Simular llamada a API
      await new Promise(resolve => setTimeout(resolve, 1000))
      
      if (tipo === 'crear') {
        const nuevoUsuario: Usuario = {
          ...formData as Usuario,
          id: Date.now().toString(),
          fechaRegistro: new Date().toISOString().split('T')[0]
        }
        onGuardar(nuevoUsuario)
      } else if (tipo === 'editar' && usuario) {
        const usuarioActualizado: Usuario = {
          ...usuario,
          ...formData
        }
        onGuardar(usuarioActualizado)
      }
      
      onClose()
    } catch (error) {
      console.error('Error al guardar usuario:', error)
    } finally {
      setCargando(false)
    }
  }

  const manejarEliminar = async () => {
    if (!usuario) return
    
    setCargando(true)
    
    try {
      // Simular llamada a API
      await new Promise(resolve => setTimeout(resolve, 1000))
      onEliminar(usuario.id)
      onClose()
    } catch (error) {
      console.error('Error al eliminar usuario:', error)
    } finally {
      setCargando(false)
    }
  }

  const sincronizarRolDerivado = (siguiente: Partial<UsuarioConPassword>): Partial<UsuarioConPassword> => ({
    ...siguiente,
    rol: usuariosService.derivarRolDesdeContexto(
      siguiente.rol_global,
      siguiente.tipo_organizacion,
      siguiente.rol_organizacion
    )
  })

  const manejarCambio = (campo: keyof Usuario | 'password', valor: any) => {
    setFormData(prev => {
      let siguiente: Partial<UsuarioConPassword> = { ...prev, [campo]: valor }

      if (campo === 'tipo_organizacion') {
        if (valor !== 'institucion') {
          siguiente.id_institucion = undefined
        }
        if (valor !== 'empresa') {
          siguiente.id_organizacion = undefined
        }
        if (!valor) {
          siguiente.rol_organizacion = undefined
        } else {
          siguiente.rol_global = 'usuario'
          siguiente.rol_organizacion =
            valor === 'empresa'
              ? 'usuario'
              : valor === 'institucion'
                ? 'usuario'
                : undefined
        }
      }

      if (campo === 'id_institucion') {
        const institucionSeleccionada = institucionesAcademicas.find(
          (inst) => Number(inst.id_constitucion) === Number(valor)
        )
        if (institucionSeleccionada) {
          siguiente.empresa = institucionSeleccionada.nombre
        }
      }

      if (campo === 'id_organizacion') {
        const organizacionSeleccionada = organizacionesEmpresa.find(
          (org) => Number(org.id_organizacion ?? org.id) === Number(valor)
        )
        if (organizacionSeleccionada) {
          siguiente.empresa = organizacionSeleccionada.nombre
        }
      }

      if (campo === 'rol_global' && valor !== 'usuario') {
        siguiente.tipo_organizacion = undefined
        siguiente.rol_organizacion = undefined
        siguiente.id_institucion = undefined
        siguiente.id_organizacion = undefined
      }

      if (campo === 'rol_global' && valor === 'usuario' && siguiente.tipo_organizacion === 'institucion' && !siguiente.rol_organizacion) {
          siguiente.rol_organizacion = 'usuario'
      }

      return sincronizarRolDerivado(siguiente)
    })
    if (errores[campo]) {
      setErrores(prev => ({ ...prev, [campo]: '' }))
    }
  }

  const obtenerTitulo = () => {
    switch (tipo) {
      case 'ver':
        return 'Detalles del Usuario'
      case 'editar':
        return 'Editar Usuario'
      case 'crear':
        return 'Crear Nuevo Usuario'
      case 'eliminar':
        return 'Eliminar Usuario'
      default:
        return 'Usuario'
    }
  }

  const esLectura = tipo === 'ver'
  const esUsuarioInstitucion = formData.tipo_organizacion === 'institucion'
  const esUsuarioEmpresa = formData.tipo_organizacion === 'empresa'
  const rolesDisponiblesActuales = esUsuarioInstitucion
    ? ROLES_INSTITUCIONALES
    : esUsuarioEmpresa
      ? ROLES_EMPRESARIALES
      : ROLES_GLOBALES
  const formIdPrefix = 'modal-usuario'
  const descripcionRolActual =
    rolesDisponiblesActuales.find((rol) =>
      esUsuarioInstitucion || esUsuarioEmpresa
        ? rol.value === (formData.rol_organizacion || 'usuario')
        : rol.value === (formData.rol_global || 'usuario')
    )?.description || 'Selecciona el rol con base en el alcance real del usuario.'

  if (!isOpen) return null

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
              {obtenerTitulo()}
            </h2>
            <button
              onClick={onClose}
              aria-label="Cerrar modal de usuario"
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {tipo === 'eliminar' ? (
              <div className="text-center">
                <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  ¿Estás seguro de eliminar este usuario?
                </h3>
                <p className="text-gray-600 dark:text-gray-400 mb-6">
                  Esta acción no se puede deshacer. Se eliminará permanentemente el usuario{' '}
                  <span className="font-semibold">{usuario?.nombre} {usuario?.apellido}</span> y todos sus datos asociados.
                </p>
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={onClose}
                    disabled={cargando}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={manejarEliminar}
                    disabled={cargando}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg disabled:opacity-50 flex items-center"
                  >
                    {cargando && (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    )}
                    Eliminar Usuario
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={manejarSubmit} className="space-y-6">
                {/* Avatar */}
                <div className="flex justify-center">
                  <div className="relative">
                    {formData.avatar ? (
                      <img
                        src={formData.avatar}
                        alt="Avatar"
                        className="h-20 w-20 rounded-full object-cover"
                      />
                    ) : (
                      <div className="h-20 w-20 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                        <UserIcon className="h-10 w-10 text-gray-500 dark:text-gray-400" />
                      </div>
                    )}
                    {!esLectura && (
                      <button
                        type="button"
                        aria-label="Agregar o cambiar avatar del usuario"
                        className="absolute bottom-0 right-0 bg-blue-600 text-white rounded-full p-1 hover:bg-blue-700"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>

                {/* Información personal */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={`${formIdPrefix}-nombre`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nombre *
                    </label>
                    <div className="relative">
                      <UserIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        id={`${formIdPrefix}-nombre`}
                        name="nombre"
                        type="text"
                        value={formData.nombre || ''}
                        onChange={(e) => manejarCambio('nombre', e.target.value)}
                        disabled={esLectura}
                        aria-invalid={Boolean(errores.nombre)}
                        aria-describedby={errores.nombre ? `${formIdPrefix}-nombre-error` : undefined}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errores.nombre ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        } ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                        placeholder="Nombre del usuario"
                      />
                    </div>
                    {errores.nombre && (
                      <p id={`${formIdPrefix}-nombre-error`} role="alert" className="mt-1 text-sm text-red-600">{errores.nombre}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor={`${formIdPrefix}-apellido`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Apellido *
                    </label>
                    <input
                      id={`${formIdPrefix}-apellido`}
                      name="apellido"
                      type="text"
                      value={formData.apellido || ''}
                      onChange={(e) => manejarCambio('apellido', e.target.value)}
                      disabled={esLectura}
                      aria-invalid={Boolean(errores.apellido)}
                      aria-describedby={errores.apellido ? `${formIdPrefix}-apellido-error` : undefined}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                        errores.apellido ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                      } ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                      placeholder="Apellido del usuario"
                    />
                    {errores.apellido && (
                      <p id={`${formIdPrefix}-apellido-error`} role="alert" className="mt-1 text-sm text-red-600">{errores.apellido}</p>
                    )}
                  </div>
                </div>

                {/* Email y teléfono */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor={`${formIdPrefix}-email`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Email *
                    </label>
                    <div className="relative">
                      <EnvelopeIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        id={`${formIdPrefix}-email`}
                        name="email"
                        type="email"
                        value={formData.email || ''}
                        onChange={(e) => manejarCambio('email', e.target.value)}
                        disabled={esLectura}
                        aria-invalid={Boolean(errores.email)}
                        aria-describedby={errores.email ? `${formIdPrefix}-email-error` : undefined}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errores.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        } ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                        placeholder="email@ejemplo.com"
                      />
                    </div>
                    {errores.email && (
                      <p id={`${formIdPrefix}-email-error`} role="alert" className="mt-1 text-sm text-red-600">{errores.email}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor={`${formIdPrefix}-telefono`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Teléfono
                    </label>
                    <div className="relative">
                      <PhoneIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <input
                        id={`${formIdPrefix}-telefono`}
                        name="telefono"
                        type="tel"
                        value={formData.telefono || ''}
                        onChange={(e) => manejarCambio('telefono', e.target.value)}
                        disabled={esLectura}
                        aria-invalid={Boolean(errores.telefono)}
                        aria-describedby={errores.telefono ? `${formIdPrefix}-telefono-error` : undefined}
                        className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errores.telefono ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        } ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                        placeholder="+57 300 123 4567"
                      />
                    </div>
                    {errores.telefono && (
                      <p id={`${formIdPrefix}-telefono-error`} role="alert" className="mt-1 text-sm text-red-600">{errores.telefono}</p>
                    )}
                  </div>
                </div>

                {/* Empresa */}
                <div>
                  <label htmlFor={`${formIdPrefix}-empresa`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Empresa
                  </label>
                  <div className="relative">
                    <BuildingOfficeIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input
                      id={`${formIdPrefix}-empresa`}
                      name="empresa"
                      type="text"
                      value={formData.empresa || ''}
                      onChange={(e) => manejarCambio('empresa', e.target.value)}
                      disabled={esLectura}
                      className={`w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                        esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''
                      }`}
                      placeholder="Nombre de la empresa"
                    />
                  </div>
                </div>

                {/* Contraseña (solo para crear) */}
                {tipo === 'crear' && (
                  <div>
                    <label htmlFor={`${formIdPrefix}-password`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Contraseña *
                    </label>
                    <div className="relative">
                      <input
                        id={`${formIdPrefix}-password`}
                        name="password"
                        type={mostrarPassword ? 'text' : 'password'}
                        value={formData.password || ''}
                        onChange={(e) => manejarCambio('password', e.target.value)}
                        aria-invalid={Boolean(errores.password)}
                        aria-describedby={errores.password ? `${formIdPrefix}-password-error` : undefined}
                        className={`w-full px-4 py-2 pr-10 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          errores.password ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                        }`}
                        placeholder="Contraseña del usuario"
                      />
                      <button
                        type="button"
                        onClick={() => setMostrarPassword(!mostrarPassword)}
                        aria-label={mostrarPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {mostrarPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                    {errores.password && (
                      <p id={`${formIdPrefix}-password-error`} role="alert" className="mt-1 text-sm text-red-600">{errores.password}</p>
                    )}
                  </div>
                )}

                {/* Configuración de cuenta */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label htmlFor={`${formIdPrefix}-tipo-organizacion`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ambito
                    </label>
                    <select
                      id={`${formIdPrefix}-tipo-organizacion`}
                      name="tipo_organizacion"
                      value={formData.tipo_organizacion || ''}
                      onChange={(e) => manejarCambio('tipo_organizacion', e.target.value || undefined)}
                      disabled={esLectura || esUsuarioEmpresa}
                      className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                        esLectura || esUsuarioEmpresa ? 'bg-gray-50 dark:bg-gray-600' : ''
                      }`}
                    >
                      <option value="">Global</option>
                      <option value="institucion">Institucion</option>
                      <option value="empresa">Empresa</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor={`${formIdPrefix}-rol-global`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Rol Global
                    </label>
                    <div className="relative">
                      <ShieldCheckIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                      <select
                        id={`${formIdPrefix}-rol-global`}
                        name="rol_global"
                        value={formData.rol_global || 'usuario'}
                        onChange={(e) => manejarCambio('rol_global', e.target.value)}
                        disabled={esLectura || !!formData.tipo_organizacion}
                        className={`w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          esLectura || !!formData.tipo_organizacion ? 'bg-gray-50 dark:bg-gray-600' : ''
                        }`}
                      >
                        {ROLES_GLOBALES.map((rol) => (
                          <option key={rol.value} value={rol.value}>
                            {rol.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    {!esUsuarioInstitucion && !esUsuarioEmpresa && (
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {descripcionRolActual}
                      </p>
                    )}
                  </div>

                  {esUsuarioInstitucion && (
                    <div>
                      <label htmlFor={`${formIdPrefix}-rol-institucional`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Rol Institucional
                      </label>
                      <select
                        id={`${formIdPrefix}-rol-institucional`}
                        name="rol_organizacion"
                        value={formData.rol_organizacion || 'usuario'}
                        onChange={(e) => manejarCambio('rol_organizacion', e.target.value)}
                        disabled={esLectura}
                        className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''
                        }`}
                      >
                        {ROLES_INSTITUCIONALES.map((rol) => (
                          <option key={rol.value} value={rol.value}>
                            {rol.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {descripcionRolActual}
                      </p>
                    </div>
                  )}

                  {esUsuarioEmpresa && (
                    <div>
                      <label htmlFor={`${formIdPrefix}-rol-empresarial`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Rol Empresarial
                      </label>
                      <select
                        id={`${formIdPrefix}-rol-empresarial`}
                        name="rol_organizacion"
                        value={formData.rol_organizacion || 'usuario'}
                        onChange={(e) => manejarCambio('rol_organizacion', e.target.value)}
                        disabled={esLectura}
                        className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                          esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''
                        }`}
                      >
                        {ROLES_EMPRESARIALES.map((rol) => (
                          <option key={rol.value} value={rol.value}>
                            {rol.label}
                          </option>
                        ))}
                      </select>
                      <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                        {descripcionRolActual}
                      </p>
                    </div>
                  )}

                  {esUsuarioInstitucion && (
                    <div>
                      <label htmlFor={`${formIdPrefix}-institucion`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Institución *
                      </label>
                      <div className="relative">
                        <BuildingOfficeIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <select
                          id={`${formIdPrefix}-institucion`}
                          name="id_institucion"
                          value={formData.id_institucion || ''}
                          onChange={(e) => manejarCambio('id_institucion', Number(e.target.value))}
                          disabled={esLectura}
                          aria-invalid={Boolean(errores.id_institucion)}
                          aria-describedby={errores.id_institucion ? `${formIdPrefix}-institucion-error` : undefined}
                          className={`w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                            errores.id_institucion ? 'border-red-500' : ''
                          } ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                        >
                          <option value="">Seleccione una institución</option>
                          {institucionesAcademicas.map((inst) => (
                            <option key={inst.id_constitucion} value={inst.id_constitucion}>
                              {inst.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                      {errores.id_institucion && (
                        <p id={`${formIdPrefix}-institucion-error`} role="alert" className="mt-1 text-sm text-red-600">{errores.id_institucion}</p>
                      )}
                    </div>
                  )}

                  {esUsuarioEmpresa && (
                    <div>
                      <label htmlFor={`${formIdPrefix}-organizacion`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Empresa *
                      </label>
                      <div className="relative">
                        <BuildingOfficeIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                        <select
                          id={`${formIdPrefix}-organizacion`}
                          name="id_organizacion"
                          value={formData.id_organizacion || ''}
                          onChange={(e) => manejarCambio('id_organizacion', Number(e.target.value))}
                          disabled={esLectura}
                          aria-invalid={Boolean(errores.id_organizacion)}
                          aria-describedby={errores.id_organizacion ? `${formIdPrefix}-organizacion-error` : undefined}
                          className={`w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                            errores.id_organizacion ? 'border-red-500' : ''
                          } ${esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''}`}
                        >
                          <option value="">Seleccione una empresa</option>
                          {organizacionesEmpresa.map((org) => (
                            <option key={org.id_organizacion ?? org.id} value={org.id_organizacion ?? org.id}>
                              {org.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                      {errores.id_organizacion && (
                        <p id={`${formIdPrefix}-organizacion-error`} role="alert" className="mt-1 text-sm text-red-600">{errores.id_organizacion}</p>
                      )}
                    </div>
                  )}

                  <div>
                    <label htmlFor={`${formIdPrefix}-estado`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Estado
                    </label>
                    <select
                      id={`${formIdPrefix}-estado`}
                      name="estado"
                      value={formData.estado || 'activo'}
                      onChange={(e) => manejarCambio('estado', e.target.value)}
                      disabled={esLectura}
                      className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                        esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''
                      }`}
                    >
                      <option value="activo">Activo</option>
                      <option value="inactivo">Inactivo</option>
                      <option value="suspendido">Suspendido</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor={`${formIdPrefix}-suscripcion`} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Suscripción
                    </label>
                    <select
                      id={`${formIdPrefix}-suscripcion`}
                      name="suscripcion"
                      value={formData.suscripcion || 'basica'}
                      onChange={(e) => manejarCambio('suscripcion', e.target.value)}
                      disabled={esLectura}
                      className={`w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white ${
                        esLectura ? 'bg-gray-50 dark:bg-gray-600' : ''
                      }`}
                    >
                      <option value="basica">Básica</option>
                      <option value="premium">Premium</option>
                      <option value="empresarial">Empresarial</option>
                      <option value="institucional">Institucional</option>
                    </select>
                  </div>
                </div>

                <div className="rounded-lg bg-gray-50 px-4 py-3 text-sm text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  Rol efectivo: <span className="font-medium text-gray-900 dark:text-white">{usuariosService.formatearEtiquetaRol(sincronizarRolDerivado(formData) as Usuario)}</span>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Catalogo de Roles del Sistema</h4>
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                      Un administrador global puede consultar aqui todos los roles disponibles antes de asignar el contexto correcto.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
                    {[
                      { titulo: 'Roles Globales', opciones: ROLES_GLOBALES, color: 'border-purple-200 bg-purple-50 dark:border-purple-900/40 dark:bg-purple-900/10' },
                      { titulo: 'Roles Institucionales', opciones: ROLES_INSTITUCIONALES, color: 'border-orange-200 bg-orange-50 dark:border-orange-900/40 dark:bg-orange-900/10' },
                      { titulo: 'Roles Empresariales', opciones: ROLES_EMPRESARIALES, color: 'border-cyan-200 bg-cyan-50 dark:border-cyan-900/40 dark:bg-cyan-900/10' },
                    ].map((grupo) => (
                      <div key={grupo.titulo} className={`rounded-lg border p-4 ${grupo.color}`}>
                        <h5 className="text-sm font-semibold text-gray-900 dark:text-white">{grupo.titulo}</h5>
                        <div className="mt-3 space-y-3">
                          {grupo.opciones.map((rol) => (
                            <div key={`${grupo.titulo}-${rol.value}`} className="rounded-md bg-white/70 p-3 dark:bg-gray-900/20">
                              <p className="text-sm font-medium text-gray-900 dark:text-white">{rol.label}</p>
                              <p className="mt-1 text-xs text-gray-600 dark:text-gray-300">{rol.description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Verificación */}
                {!esLectura && (
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id={`${formIdPrefix}-verificado`}
                      name="verificado"
                      checked={formData.verificado || false}
                      onChange={(e) => manejarCambio('verificado', e.target.checked)}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`${formIdPrefix}-verificado`} className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Usuario verificado
                    </label>
                  </div>
                )}

                {/* Información adicional (solo en modo ver) */}
                {esLectura && usuario && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                    <h4 className="font-medium text-gray-900 dark:text-white">Información adicional</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Fecha de registro:</span>
                        <span className="ml-2 text-gray-900 dark:text-white">
                          {new Date(usuario.fechaRegistro).toLocaleDateString('es-ES')}
                        </span>
                      </div>
                      {usuario.ultimoAcceso && (
                        <div>
                          <span className="text-gray-600 dark:text-gray-400">Último acceso:</span>
                          <span className="ml-2 text-gray-900 dark:text-white">
                            {new Date(usuario.ultimoAcceso).toLocaleDateString('es-ES')}
                          </span>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600 dark:text-gray-400">Verificado:</span>
                        <span className="ml-2">
                          {usuario.verificado ? (
                            <CheckCircleIcon className="h-4 w-4 text-green-500 inline" />
                          ) : (
                            <XMarkIcon className="h-4 w-4 text-red-500 inline" />
                          )}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Botones */}
                {!esLectura && (
                  <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={onClose}
                      disabled={cargando}
                      className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 disabled:opacity-50"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={cargando}
                      className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg disabled:opacity-50 flex items-center"
                    >
                      {cargando && (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      )}
                      {tipo === 'crear' ? 'Crear Usuario' : 'Guardar Cambios'}
                    </button>
                  </div>
                )}

                {esLectura && (
                  <div className="flex justify-end pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={onClose}
                      className="px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
                    >
                      Cerrar
                    </button>
                  </div>
                )}
              </form>
            )}
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  )
}

export default ModalUsuario
